// main/BodyContainer.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Layout, Menu, Drawer } from 'antd';
import {
  UserOutlined,
  SearchOutlined,
  AreaChartOutlined,
  CloseOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import Tz from '../TZ/TZ'; // Исправьте путь, если необходимо
import Analyze from '../analyze/analyze';
import Cabinet from '../user/Cabinet';
import AdminPanel from '../admin/AdminPanel';
import Chenglog from './chenglog'; // Новый компонент для changelog
import AppHeader from './AppHeader';
import AppFooter from './AppFooter';
import SiderSearchSnapshotButton from './SiderSearchSnapshotButton';

const { Content, Sider } = Layout;

/** Как у Ant Design dark Sider / тёмного меню */
const DARK_SIDER_BG = '#001529';

const BASE_MENU_ITEMS = [
  { key: 'search', icon: <SearchOutlined />, label: 'Поиск' },
  { key: 'analysis', icon: <AreaChartOutlined />, label: 'АНАЛИЗ' },
  { key: 'cabinet', icon: <UserOutlined />, label: 'Личный кабинет' },
];

const ADMIN_MENU_ITEM = { key: 'admin', icon: <SettingOutlined />, label: 'Админка' };
const STAFF_ROLES = ['admin', 'manager'];

const BodyContainer = ({ handleLogout }) => {
  const user = useSelector((state) => state.interface.username);
  const role = useSelector((state) => state.interface.role);
  const menuItems =
    STAFF_ROLES.includes(role) ? [...BASE_MENU_ITEMS, ADMIN_MENU_ITEM] : BASE_MENU_ITEMS;
  const profile = useSelector((state) => state.interface.profile);
  const socketStatus = useSelector((state) => state.interface.socketStatus);
  const [collapsed, setCollapsed] = useState(true);
  const [selectedMenuKey, setSelectedMenuKey] = useState("search");
  const [chenglogVisible, setChenglogVisible] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobileLayout, setIsMobileLayout] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 991px)').matches : false
  );
  /** На время сворачивания отключаем title-Tooltip у пунктов Ant Design Menu — иначе при сдвиге вёрстки всплывают все подписи сразу. */
  const [suppressMenuItemTooltips, setSuppressMenuItemTooltips] = useState(false);
  const menuTooltipPauseTimerRef = useRef(null);

  const pauseMenuTooltipsAfterCollapse = useCallback(() => {
    setSuppressMenuItemTooltips(true);
    if (menuTooltipPauseTimerRef.current != null) {
      clearTimeout(menuTooltipPauseTimerRef.current);
    }
    menuTooltipPauseTimerRef.current = window.setTimeout(() => {
      menuTooltipPauseTimerRef.current = null;
      setSuppressMenuItemTooltips(false);
    }, 500);
  }, []);

  useEffect(() => {
    return () => {
      if (menuTooltipPauseTimerRef.current != null) {
        clearTimeout(menuTooltipPauseTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 991px)');
    const apply = () => {
      const mobile = mq.matches;
      setIsMobileLayout(mobile);
      if (mobile) {
        setCollapsed(true);
        pauseMenuTooltipsAfterCollapse();
      } else {
        setMobileMenuOpen(false);
      }
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [pauseMenuTooltipsAfterCollapse]);

  const handleMenuClick = (e) => {
    setSelectedMenuKey(e.key);
    if (e.key !== 'admin') {
      // Уходя из админки, убираем её состояние из URL, чтобы F5 не возвращал туда.
      const url = new URL(window.location.href);
      const adminKeys = Array.from(url.searchParams.keys()).filter((key) => key.startsWith('admin.'));
      if (adminKeys.length) {
        adminKeys.forEach((key) => url.searchParams.delete(key));
        window.history.replaceState(window.history.state, '', url);
      }
    }
    setMobileMenuOpen(false);
  };

  // Ссылка вида ?admin.tab=users должна открывать админку сразу после загрузки.
  useEffect(() => {
    if (!STAFF_ROLES.includes(role)) return;
    const url = new URL(window.location.href);
    if (url.searchParams.has('admin.tab')) {
      setSelectedMenuKey('admin');
    }
  }, [role]);

  const handleOpenCabinet = () => {
    handleMenuClick({ key: 'cabinet' });
  };

  // ensureDataUrl перенесена в AppHeader, локально не используется

  return (
    <Layout className="app-root-layout" style={{ minHeight: '100vh' }}>
      <AppHeader
        user={user}
        profile={profile}
        socketStatus={socketStatus}
        onOpenChangelog={() => setChenglogVisible(true)}
        onLogout={handleLogout}
        onOpenCabinet={handleOpenCabinet}
        showMobileMenu={isMobileLayout}
        onOpenMobileMenu={() => setMobileMenuOpen(true)}
      />
      <Layout className="app-main-with-sider">
        {!isMobileLayout && (
          <Sider
            collapsible
            collapsed={collapsed}
            onCollapse={(value) => {
              setCollapsed(value);
              if (value) pauseMenuTooltipsAfterCollapse();
            }}
            width={220}
            style={{ overflow: 'hidden' }}
          >
            <div className="app-sider-inner">
              <Menu
                theme="dark"
                mode="inline"
                _internalDisableMenuItemTitleTooltip={suppressMenuItemTooltips}
                // rc-menu ставит ant-menu-inline-collapsed с задержкой (state + useEffect), из‑за этого
                // на кадр остаются видимыми подписи при сворачивании. Тот же collapsed, что у Sider.
                className={collapsed ? 'ant-menu-inline-collapsed' : undefined}
                selectedKeys={[selectedMenuKey]}
                onClick={handleMenuClick}
                items={menuItems}
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  borderInlineEnd: 'none',
                }}
              />
              <SiderSearchSnapshotButton />
            </div>
          </Sider>
        )}

        <Content className="app-main-content">
          {selectedMenuKey === "search" && <Tz />}
          {selectedMenuKey === "analysis" && <Analyze />}
          {selectedMenuKey === "cabinet" && <Cabinet />}
          {selectedMenuKey === "admin" && STAFF_ROLES.includes(role) && <AdminPanel />}
        </Content>
      </Layout>

      <AppFooter />

      {/* Модальное окно с changelog */}
      <Chenglog
        open={chenglogVisible}
        onClose={() => setChenglogVisible(false)}
      />

      <Drawer
        title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>Меню</span>}
        placement="left"
        width={260}
        open={isMobileLayout && mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        rootClassName="app-mobile-nav-drawer"
        styles={{
          content: { background: DARK_SIDER_BG },
          header: {
            background: DARK_SIDER_BG,
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          },
          body: { padding: 0, background: DARK_SIDER_BG },
        }}
        closeIcon={<CloseOutlined style={{ color: 'rgba(255,255,255,0.65)', fontSize: 14 }} />}
        zIndex={1002}
      >
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedMenuKey]}
          onClick={handleMenuClick}
          items={menuItems}
          style={{ borderInlineEnd: 'none', background: DARK_SIDER_BG }}
        />
      </Drawer>
    </Layout>
  );
};

export default BodyContainer;
